import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, Boxes, Edit3, Eye, MapPin, PackagePlus, RotateCcw, Save, Search, Trash2, X } from "lucide-react";
import { api } from "../../services/api";
import { resolveProductImage } from "../../utils/productAssets";
import { InsightCard, PaginationControls, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { usePagination } from "./usePagination";
import { ToastStack, useToast } from "./useToast";

const emptyWarehouse = {
  name: "",
  code: "",
  region: "",
  status: "active",
  capacityPercent: 0,
  pickPriority: 10,
  addressLine1: "",
  city: "",
  state: "",
  postalCode: "",
  country: "United States",
};

function statusTone(status) {
  if (["active", "available"].includes(status)) return "good";
  if (status === "needs_review") return "warn";
  return "neutral";
}

function stockTone(stock) {
  return Number(stock.quantity) <= Number(stock.reorderPoint) ? "warn" : "good";
}

function locationLabel(warehouse) {
  return [warehouse?.address?.city, warehouse?.address?.state, warehouse?.address?.country].filter(Boolean).join(", ") || "No address";
}

function warehouseToForm(warehouse = {}) {
  return {
    name: warehouse.name || "",
    code: warehouse.code || "",
    region: warehouse.region || "",
    status: warehouse.status || "active",
    capacityPercent: warehouse.capacityPercent ?? 0,
    pickPriority: warehouse.pickPriority ?? 10,
    addressLine1: warehouse.address?.line1 || "",
    city: warehouse.address?.city || "",
    state: warehouse.address?.state || "",
    postalCode: warehouse.address?.postalCode || "",
    country: warehouse.address?.country || "United States",
  };
}

function warehousePayloadFromForm(form) {
  return {
    name: form.name,
    code: form.code,
    region: form.region,
    status: form.status,
    capacityPercent: Number(form.capacityPercent || 0),
    pickPriority: Number(form.pickPriority || 100),
    address: {
      line1: form.addressLine1,
      city: form.city,
      state: form.state,
      postalCode: form.postalCode,
      country: form.country,
    },
  };
}

function normalizeProduct(product) {
  return {
    id: product._id || product.id || product.sku,
    sku: product.sku || "",
    name: product.name || product.productName || "Untitled product",
    category: product.category || product.brandLine || "Uncategorized",
    image: product.image,
    stock: Number(product.stock || 0),
  };
}

export default function Warehouse() {
  const [activeTab, setActiveTab] = useState("warehouses");
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");
  const [stockSearch, setStockSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [warehouseForm, setWarehouseForm] = useState(emptyWarehouse);
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);
  const [editingWarehouseId, setEditingWarehouseId] = useState("");
  const [savingWarehouse, setSavingWarehouse] = useState(false);
  const [deletingWarehouseId, setDeletingWarehouseId] = useState("");
  const [transferForm, setTransferForm] = useState({ fromWarehouseId: "", toWarehouseId: "", sku: "", quantity: 1, toBin: "" });
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState(() => new Set());
  const [selectedProductDetails, setSelectedProductDetails] = useState({});
  const [bulkBin, setBulkBin] = useState("");
  const [bulkQuantity, setBulkQuantity] = useState(1);
  const [bulkReorderPoint, setBulkReorderPoint] = useState(0);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const { dismiss, toast, toasts } = useToast();

  const loadWarehouses = useCallback(async () => {
    const data = await api.get("/warehouses");
    return data.warehouses || [];
  }, []);

  const { data: warehouses, error, loading, reload, setData: setWarehouses } = useAdminResource(loadWarehouses, []);
  const selectedWarehouse = warehouses.find((warehouse) => warehouse._id === selectedId) || warehouses[0];
  const editingWarehouse = warehouses.find((warehouse) => warehouse._id === editingWarehouseId);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(async () => {
      setProductsLoading(true);
      try {
        const params = new URLSearchParams({ limit: "24", page: "1" });
        const term = productSearch.trim();
        if (term) params.set("search", term);
        const data = await api.get(`/products?${params.toString()}`);
        if (active) {
          const nextProducts = (data.products || []).map(normalizeProduct);
          setProducts(nextProducts);
          setSelectedProductDetails((current) => {
            const next = { ...current };
            nextProducts.forEach((product) => {
              next[product.id] = product;
            });
            return next;
          });
        }
      } catch (err) {
        if (active) toast(`Products unavailable: ${err.message}`, "warning");
      } finally {
        if (active) setProductsLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [productSearch, toast]);

  useEffect(() => {
    if (error && !loading) toast(`Unable to load warehouses: ${error}`, "warning");
  }, [error, loading, toast]);

  useEffect(() => {
    if (!transferForm.fromWarehouseId && warehouses[0]?._id) {
      setTransferForm((current) => ({
        ...current,
        fromWarehouseId: warehouses[0]._id,
        toWarehouseId: warehouses[1]?._id || warehouses[0]._id,
      }));
    }
  }, [transferForm.fromWarehouseId, warehouses]);

  const visibleWarehouses = useMemo(() => {
    const term = search.toLowerCase();
    return warehouses.filter((warehouse) =>
      [warehouse.name, warehouse.code, warehouse.region, warehouse.status, locationLabel(warehouse)]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [search, warehouses]);

  const allStock = useMemo(() => {
    return warehouses.flatMap((warehouse) =>
      (warehouse.stock || []).map((stock) => ({
        ...stock,
        warehouseId: warehouse._id,
        warehouseName: warehouse.name,
        warehouseCode: warehouse.code,
      }))
    );
  }, [warehouses]);

  const visibleStock = useMemo(() => {
    return allStock.filter((stock) =>
      [stock.sku, stock.productName, stock.bin, stock.warehouseName, stock.warehouseCode]
        .join(" ")
        .toLowerCase()
        .includes(stockSearch.toLowerCase())
    );
  }, [allStock, stockSearch]);

  const productRows = products.slice(0, 12);
  const selectedProducts = Array.from(selectedProductIds)
    .map((productId) => selectedProductDetails[productId])
    .filter((product) => product && product.sku);
  const selectableProductRows = productRows.filter((product) => product.sku);
  const allVisibleProductsSelected = selectableProductRows.length > 0 && selectableProductRows.every((product) => selectedProductIds.has(product.id));

  const transfers = useMemo(() => {
    return warehouses.flatMap((warehouse) =>
      (warehouse.transfers || []).map((transfer) => ({
        ...transfer,
        warehouseName: warehouse.name,
      }))
    );
  }, [warehouses]);

  const metrics = {
    warehouses: warehouses.length,
    skus: new Set(allStock.map((item) => item.sku)).size,
    lowStock: allStock.filter((item) => Number(item.quantity) <= Number(item.reorderPoint)).length,
    selectedProducts: selectedProductIds.size,
  };
  const warehousePagination = usePagination(visibleWarehouses, [search], 10);
  const stockPagination = usePagination(visibleStock, [activeTab, stockSearch], 10);
  const stockSummary = useMemo(() => {
    const rows = stockPagination.paginatedItems || [];
    return {
      rows: rows.length,
      lowStock: rows.filter((item) => Number(item.quantity) <= Number(item.reorderPoint)).length,
      warehouses: new Set(rows.map((item) => item.warehouseName)).size,
    };
  }, [stockPagination.paginatedItems]);

  const updateWarehouseForm = (event) => {
    const { name, value } = event.target;
    setWarehouseForm((current) => ({ ...current, [name]: value }));
  };

  const updateTransferForm = (event) => {
    setTransferForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const resetWarehouseForm = () => {
    setWarehouseForm(emptyWarehouse);
    setEditingWarehouseId("");
  };

  const closeWarehouseModal = () => {
    resetWarehouseForm();
    setIsWarehouseModalOpen(false);
  };

  const resetVisibleWarehouseForm = () => {
    setWarehouseForm(editingWarehouse ? warehouseToForm(editingWarehouse) : emptyWarehouse);
  };

  const startCreateWarehouse = () => {
    resetWarehouseForm();
    setIsWarehouseModalOpen(true);
    setActiveTab("warehouses");
  };

  const startEditWarehouse = (warehouse) => {
    setSelectedId(warehouse._id);
    setEditingWarehouseId(warehouse._id);
    setWarehouseForm(warehouseToForm(warehouse));
    setIsWarehouseModalOpen(true);
    setActiveTab("warehouses");
  };

  const viewWarehouseStock = (warehouse) => {
    setSelectedId(warehouse._id);
    setStockSearch(warehouse.code || warehouse.name || "");
    setActiveTab("stock");
  };

  const openStockAssignment = (warehouse) => {
    setSelectedId(warehouse._id);
    setBulkBin(warehouse.code || "Primary");
    setIsStockModalOpen(true);
    setActiveTab("stock");
  };

  const startTransferFromWarehouse = (warehouse) => {
    const fallbackTarget = warehouses.find((item) => item._id !== warehouse._id)?._id || warehouse._id;
    setSelectedId(warehouse._id);
    setTransferForm((current) => ({
      ...current,
      fromWarehouseId: warehouse._id,
      toWarehouseId: current.toWarehouseId && current.toWarehouseId !== warehouse._id ? current.toWarehouseId : fallbackTarget,
    }));
    setActiveTab("transfers");
  };

  const toggleProduct = (productId) => {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleVisibleProducts = () => {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      selectableProductRows.forEach((product) => {
        if (allVisibleProductsSelected) next.delete(product.id);
        else next.add(product.id);
      });
      return next;
    });
  };

  const saveWarehouse = async (event) => {
    event.preventDefault();
    setSavingWarehouse(true);

    try {
      const payload = warehousePayloadFromForm(warehouseForm);
      const data = editingWarehouseId
        ? await api.patch(`/warehouses/${editingWarehouseId}`, payload)
        : await api.post("/warehouses", payload);

      setWarehouses((current) =>
        editingWarehouseId
          ? current.map((item) => (item._id === editingWarehouseId ? data.warehouse : item))
          : [data.warehouse, ...current]
      );
      setSelectedId(data.warehouse._id);
      setWarehouseForm(emptyWarehouse);
      setEditingWarehouseId("");
      setIsWarehouseModalOpen(false);
      toast(`${editingWarehouseId ? "Updated" : "Created"} ${data.warehouse.name}`, "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSavingWarehouse(false);
    }
  };

  const updateWarehouseStatus = async (warehouseId, status) => {
    try {
      const data = await api.patch(`/warehouses/${warehouseId}`, { status });
      setWarehouses((current) => current.map((item) => (item._id === warehouseId ? data.warehouse : item)));
      if (editingWarehouseId === warehouseId) setWarehouseForm(warehouseToForm(data.warehouse));
      toast(`Updated ${data.warehouse.name}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const deleteWarehouse = async (warehouse) => {
    if (!window.confirm(`Delete warehouse ${warehouse.name}? This is only allowed when the warehouse has no stock, transfers, or product assignments.`)) return;

    setDeletingWarehouseId(warehouse._id);
    try {
      await api.delete(`/warehouses/${warehouse._id}`);
      const nextWarehouses = warehouses.filter((item) => item._id !== warehouse._id);
      setWarehouses(nextWarehouses);
      if (selectedId === warehouse._id) setSelectedId(nextWarehouses[0]?._id || "");

      if (editingWarehouseId === warehouse._id) closeWarehouseModal();

      toast(`Deleted ${warehouse.name}`, "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setDeletingWarehouseId("");
    }
  };

  const assignSelectedProducts = async (event) => {
    event.preventDefault();
    if (!selectedWarehouse?._id || !selectedProducts.length) return;

    try {
      const results = await Promise.allSettled(
        selectedProducts.map((product) =>
          api.post(`/warehouses/${selectedWarehouse._id}/stock`, {
            sku: product.sku,
            productName: product.name,
            bin: bulkBin,
            quantity: Number(bulkQuantity || 0),
            reorderPoint: Number(bulkReorderPoint || 0),
          })
        )
      );

      const successful = results.filter((result) => result.status === "fulfilled").length;
      const failed = results.length - successful;
      await reload();
      setSelectedProductIds(new Set());
      setSelectedProductDetails((current) => {
        const next = { ...current };
        selectedProducts.forEach((product) => {
          next[product.id] = product;
        });
        return next;
      });
      setIsStockModalOpen(false);
      if (failed) toast(`${successful} products assigned, ${failed} failed`, "warning");
      else toast(`${successful} products assigned to ${selectedWarehouse.name}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const transferStock = async (event) => {
    event.preventDefault();
    try {
      await api.post("/warehouses/transfer", {
        ...transferForm,
        quantity: Number(transferForm.quantity || 1),
      });
      await reload();
      toast("Stock transfer completed", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  return (
    <div className="space-y-6">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-black uppercase tracking-wide text-[#4391d1]">Warehouse Locations</p>
          <h1 className="mt-2 text-3xl font-black text-gray-900">Warehouse locations, stock bins, and transfers</h1>
          <p className="mt-2 max-w-3xl text-sm font-medium text-gray-500">
            Manage ATUM-style locations, bin-level stock, product assignments, and warehouse transfers from one screen.
          </p>
        </div>
        <WorkflowToolbar
          actions={[
            { label: "Refresh", onClick: reload },
            { label: "Add Warehouse", primary: true, onClick: startCreateWarehouse },
          ]}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <InsightCard title="Warehouses" value={metrics.warehouses} note="Fulfillment locations" tone="info" />
        <InsightCard title="Unique SKUs" value={metrics.skus} note="Stocked across locations" tone="good" />
        <InsightCard title="Low Stock Bins" value={metrics.lowStock} note="At or under reorder point" tone="warn" />
        <InsightCard title="Selected Products" value={metrics.selectedProducts} note="Ready for location assignment" tone="money" />
      </div>

      <div className="flex flex-wrap gap-2 rounded-2xl bg-white p-2 shadow-sm">
        {[
          ["Warehouses", "warehouses", MapPin],
          ["Stock Per Location", "stock", Boxes],
          ["Transfers", "transfers", ArrowRightLeft],
        ].map(([label, value, Icon]) => (
          <button
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-black ${
              activeTab === value ? "bg-[#4391d1] text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
            key={value}
            onClick={() => setActiveTab(value)}
            type="button"
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {activeTab === "warehouses" && (
        <div className="space-y-6">
          <section className="rounded-2xl bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <h2 className="text-xl font-black text-gray-900">Warehouse List</h2>
              <input
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold outline-none focus:border-[#4391d1]"
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search warehouses"
                value={search}
              />
            </div>
            <div className="mt-5 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-left text-sm">
                <thead>
                  <tr className="text-xs font-black uppercase text-gray-400">
                    <th className="px-3 py-3">Warehouse</th>
                    <th className="px-3 py-3">Location</th>
                    <th className="px-3 py-3">SKUs</th>
                    <th className="px-3 py-3">Capacity</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {warehousePagination.paginatedItems.map((warehouse) => (
                    <tr className={`hover:bg-blue-50/40 ${selectedWarehouse?._id === warehouse._id ? "bg-blue-50/50" : ""}`} key={warehouse._id}>
                      <td className="px-3 py-4">
                        <button className="text-left font-black text-gray-900" onClick={() => setSelectedId(warehouse._id)} type="button">
                          {warehouse.name}
                        </button>
                        <p className="text-xs font-bold text-gray-400">Bin/Drawer: {warehouse.code}</p>
                      </td>
                      <td className="px-3 py-4 font-semibold text-gray-600">{locationLabel(warehouse)}</td>
                      <td className="px-3 py-4 font-black text-gray-700">{warehouse.stock?.length || 0}</td>
                      <td className="px-3 py-4 font-black text-gray-700">{warehouse.capacityPercent || 0}%</td>
                      <td className="px-3 py-4">
                        <StatusPill tone={statusTone(warehouse.status)}>{String(warehouse.status).replaceAll("_", " ")}</StatusPill>
                      </td>
                      <td className="px-3 py-4">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <select
                            aria-label={`Change ${warehouse.name} status`}
                            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-bold text-gray-700 outline-none focus:border-[#4391d1]"
                            onChange={(event) => updateWarehouseStatus(warehouse._id, event.target.value)}
                            value={warehouse.status}
                          >
                            <option value="active">Active</option>
                            <option value="available">Available</option>
                            <option value="needs_review">Needs review</option>
                            <option value="inactive">Inactive</option>
                          </select>
                          <button
                            aria-label={`View stock for ${warehouse.name}`}
                            className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:border-[#4391d1] hover:text-[#4391d1]"
                            onClick={() => viewWarehouseStock(warehouse)}
                            title="View stock"
                            type="button"
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            aria-label={`Assign products to ${warehouse.name}`}
                            className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:border-[#4391d1] hover:text-[#4391d1]"
                            onClick={() => openStockAssignment(warehouse)}
                            title="Assign products"
                            type="button"
                          >
                            <PackagePlus size={15} />
                          </button>
                          <button
                            aria-label={`Transfer stock from ${warehouse.name}`}
                            className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:border-[#4391d1] hover:text-[#4391d1]"
                            onClick={() => startTransferFromWarehouse(warehouse)}
                            title="Transfer stock"
                            type="button"
                          >
                            <ArrowRightLeft size={15} />
                          </button>
                          <button
                            aria-label={`Edit ${warehouse.name}`}
                            className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:border-[#4391d1] hover:text-[#4391d1]"
                            onClick={() => startEditWarehouse(warehouse)}
                            title="Edit warehouse"
                            type="button"
                          >
                            <Edit3 size={15} />
                          </button>
                          <button
                            aria-label={`Delete ${warehouse.name}`}
                            className="rounded-lg border border-red-100 bg-red-50 p-2 text-red-600 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={deletingWarehouseId === warehouse._id}
                            onClick={() => deleteWarehouse(warehouse)}
                            title="Delete warehouse"
                            type="button"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls pagination={warehousePagination} />
          </section>
        </div>
      )}

      {isWarehouseModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4 py-6"
          onClick={closeWarehouseModal}
        >
          <form
            aria-labelledby="warehouse-modal-title"
            aria-modal="true"
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            onSubmit={saveWarehouse}
            role="dialog"
          >
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 p-6">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-[#4391d1]">
                  <MapPin size={20} />
                </span>
                <div>
                  <p className="text-xs font-black uppercase tracking-wide text-[#4391d1]">
                    {editingWarehouseId ? "Edit Location" : "New Location"}
                  </p>
                  <h2 className="mt-1 text-xl font-black text-gray-900" id="warehouse-modal-title">
                    {editingWarehouseId ? editingWarehouse?.name || "Edit Warehouse" : "Add Warehouse"}
                  </h2>
                </div>
              </div>
              <button
                aria-label="Close warehouse form"
                className="rounded-xl border border-gray-200 p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                onClick={closeWarehouseModal}
                type="button"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-5 p-6">
              <section>
                <h3 className="text-sm font-black uppercase tracking-wide text-gray-500">Warehouse Details</h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {[
                    ["name", "Warehouse name"],
                    ["code", "Bin/Drawer"],
                    ["region", "Region"],
                  ].map(([name, placeholder]) => (
                    <input
                      className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                      key={name}
                      name={name}
                      onChange={updateWarehouseForm}
                      placeholder={placeholder}
                      required={["name", "code"].includes(name)}
                      value={warehouseForm[name]}
                    />
                  ))}
                  <select
                    className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                    name="status"
                    onChange={updateWarehouseForm}
                    value={warehouseForm.status}
                  >
                    <option value="active">Active</option>
                    <option value="available">Available</option>
                    <option value="needs_review">Needs review</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-black uppercase tracking-wide text-gray-500">
                      Capacity %
                    </span>
                    <input
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                      max="100"
                      min="0"
                      name="capacityPercent"
                      onChange={updateWarehouseForm}
                      placeholder="0"
                      type="number"
                      value={warehouseForm.capacityPercent}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-black uppercase tracking-wide text-gray-500">
                      Pick Priority
                    </span>
                    <input
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                      min="1"
                      name="pickPriority"
                      onChange={updateWarehouseForm}
                      placeholder="10"
                      type="number"
                      value={warehouseForm.pickPriority}
                    />
                  </label>
                </div>
              </section>

              <section>
                <h3 className="text-sm font-black uppercase tracking-wide text-gray-500">Address</h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <input
                    className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1] sm:col-span-2"
                    name="addressLine1"
                    onChange={updateWarehouseForm}
                    placeholder="Address"
                    value={warehouseForm.addressLine1}
                  />
                  {[
                    ["city", "City"],
                    ["state", "State"],
                    ["postalCode", "Postal code"],
                    ["country", "Country"],
                  ].map(([name, placeholder]) => (
                    <input
                      className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                      key={name}
                      name={name}
                      onChange={updateWarehouseForm}
                      placeholder={placeholder}
                      value={warehouseForm[name]}
                    />
                  ))}
                </div>
              </section>
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50 p-6 sm:flex-row sm:items-center sm:justify-between">
              <button
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-black text-gray-700 hover:bg-gray-100"
                onClick={resetVisibleWarehouseForm}
                type="button"
              >
                <RotateCcw size={16} />
                {editingWarehouseId ? "Reset Changes" : "Clear Form"}
              </button>
              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  className="rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm font-black text-gray-700 hover:bg-gray-100"
                  onClick={closeWarehouseModal}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white hover:bg-blue-600 disabled:bg-gray-300"
                  disabled={savingWarehouse}
                  type="submit"
                >
                  <Save size={16} />
                  {savingWarehouse ? "Saving..." : editingWarehouseId ? "Save Changes" : "Create Warehouse"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {activeTab === "stock" && (
        <div className="space-y-6">
          <section className="rounded-2xl bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 border-b border-gray-100 pb-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h2 className="text-xl font-black text-gray-900">Stock Per Location</h2>
                  <p className="mt-1 text-sm font-medium text-gray-500">
                    One row per SKU location, with the warehouse and bin shown together for quicker scanning.
                  </p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    className="w-full rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold outline-none focus:border-[#4391d1] lg:max-w-xs"
                    onChange={(event) => setStockSearch(event.target.value)}
                    placeholder="Search SKU, product, bin, warehouse..."
                    value={stockSearch}
                  />
                  <button
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-4 py-2 text-sm font-black text-white hover:bg-blue-600"
                    onClick={() => setIsStockModalOpen(true)}
                    type="button"
                  >
                    <PackagePlus size={16} />
                    Assign Products
                  </button>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl bg-gray-50 px-4 py-3">
                  <p className="text-[11px] font-black uppercase tracking-wide text-gray-400">Rows</p>
                  <p className="mt-1 text-lg font-black text-gray-900">{stockSummary.rows}</p>
                </div>
                <div className="rounded-xl bg-gray-50 px-4 py-3">
                  <p className="text-[11px] font-black uppercase tracking-wide text-gray-400">Warehouses</p>
                  <p className="mt-1 text-lg font-black text-gray-900">{stockSummary.warehouses}</p>
                </div>
                <div className="rounded-xl bg-gray-50 px-4 py-3">
                  <p className="text-[11px] font-black uppercase tracking-wide text-gray-400">Low stock</p>
                  <p className="mt-1 text-lg font-black text-gray-900">{stockSummary.lowStock}</p>
                </div>
              </div>
            </div>
            <div className="mt-5 grid gap-3 md:hidden">
              {stockPagination.paginatedItems.map((stock) => (
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4" key={`${stock.warehouseId}-${stock._id || stock.sku}-${stock.bin}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-black text-gray-900">{stock.productName || "Unassigned item"}</p>
                      <p className="mt-1 text-xs font-semibold text-gray-500">SKU {stock.sku}</p>
                    </div>
                    <StatusPill tone={stockTone(stock)}>{Number(stock.quantity) <= Number(stock.reorderPoint) ? "Low" : "OK"}</StatusPill>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-wide text-gray-400">Warehouse</p>
                      <p className="mt-1 text-sm font-bold text-gray-900">{stock.warehouseName}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-wide text-gray-400">Bin</p>
                      <p className="mt-1 text-sm font-bold text-gray-900">{stock.bin}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-wide text-gray-400">Quantity</p>
                      <p className="mt-1 text-sm font-black text-gray-900">{stock.quantity}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-wide text-gray-400">Reorder at</p>
                      <p className="mt-1 text-sm font-black text-gray-900">{stock.reorderPoint}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 hidden overflow-x-auto md:block">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-gray-100 text-[11px] uppercase tracking-wide text-gray-400">
                    <th className="px-3 py-3 font-black">Item</th>
                    <th className="px-3 py-3 font-black">Location</th>
                    <th className="px-3 py-3 font-black">Qty</th>
                    <th className="px-3 py-3 font-black">Reorder</th>
                    <th className="px-3 py-3 font-black">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stockPagination.paginatedItems.map((stock) => {
                    const lowStock = Number(stock.quantity) <= Number(stock.reorderPoint);
                    return (
                      <tr className="align-top hover:bg-blue-50/40" key={`${stock.warehouseId}-${stock._id || stock.sku}-${stock.bin}`}>
                        <td className="px-3 py-4">
                          <p className="font-black text-gray-900">{stock.productName || "Unassigned item"}</p>
                          <p className="mt-1 text-xs font-semibold text-gray-500">{stock.sku}</p>
                        </td>
                        <td className="px-3 py-4">
                          <p className="font-bold text-gray-900">{stock.warehouseName}</p>
                          <p className="mt-1 text-xs font-semibold text-gray-500">Bin {stock.bin}</p>
                        </td>
                        <td className="px-3 py-4">
                          <p className={`text-lg font-black ${lowStock ? "text-amber-600" : "text-emerald-700"}`}>{stock.quantity}</p>
                        </td>
                        <td className="px-3 py-4 font-bold text-gray-700">{stock.reorderPoint}</td>
                        <td className="px-3 py-4">
                          <StatusPill tone={stockTone(stock)}>
                            {lowStock ? "Needs attention" : "Healthy"}
                          </StatusPill>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationControls pagination={stockPagination} />
          </section>

          {isStockModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4 py-6" onClick={() => setIsStockModalOpen(false)}>
              <div
                className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-3xl bg-white shadow-2xl"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-4 border-b border-gray-100 p-6">
                  <div>
                    <h2 className="text-xl font-black text-gray-900">Assign Products to Location</h2>
                    <p className="mt-1 text-sm font-medium text-gray-500">
                      Search the product catalog from the backend, select items, and assign them in bulk.
                    </p>
                  </div>
                  <button
                    className="rounded-xl border border-gray-200 p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                    onClick={() => setIsStockModalOpen(false)}
                    type="button"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="grid gap-6 p-6">
                  <form className="rounded-2xl border border-gray-100 p-5" onSubmit={assignSelectedProducts}>
                    <div className="flex items-center gap-3">
                      <PackagePlus className="text-[#4391d1]" />
                      <h3 className="text-lg font-black text-gray-900">Bulk assignment</h3>
                    </div>

                    <div className="mt-5 grid gap-4">
                      <select
                        className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold"
                        onChange={(event) => setSelectedId(event.target.value)}
                        value={selectedWarehouse?._id || ""}
                      >
                        {warehouses.map((warehouse) => (
                          <option key={warehouse._id} value={warehouse._id}>{warehouse.name}</option>
                        ))}
                      </select>

                      <div className="grid gap-3 sm:grid-cols-[1fr_0.5fr_0.5fr]">
                        <label className="block">
                          <span className="mb-1 block text-[11px] font-black uppercase tracking-wide text-gray-500">
                            Location / Bin
                          </span>
                          <input
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                            name="bulkBin"
                            onChange={(event) => setBulkBin(event.target.value)}
                            placeholder="Primary"
                            required
                            value={bulkBin}
                          />
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-[11px] font-black uppercase tracking-wide text-gray-500">
                            Quantity
                          </span>
                          <input
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                            min="0"
                            onChange={(event) => setBulkQuantity(event.target.value)}
                            placeholder="0"
                            type="number"
                            value={bulkQuantity}
                          />
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-[11px] font-black uppercase tracking-wide text-gray-500">
                            Reorder
                          </span>
                          <input
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                            min="0"
                            onChange={(event) => setBulkReorderPoint(event.target.value)}
                            placeholder="0"
                            type="number"
                            value={bulkReorderPoint}
                          />
                        </label>
                      </div>

                      <div className="rounded-2xl border border-gray-100">
                        <div className="flex flex-col gap-3 border-b border-gray-100 p-3 sm:flex-row sm:items-center sm:justify-between">
                          <label className="flex items-center gap-3 text-sm font-black text-gray-700">
                            <input checked={allVisibleProductsSelected} onChange={toggleVisibleProducts} type="checkbox" />
                            Select visible products
                          </label>
                          <div className="relative">
                            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                            <input
                              className="w-full rounded-xl border border-gray-200 py-2 pl-9 pr-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                              onChange={(event) => setProductSearch(event.target.value)}
                              placeholder="Search backend products"
                              value={productSearch}
                            />
                          </div>
                        </div>

                        <div className="max-h-[360px] divide-y divide-gray-100 overflow-y-auto">
                          {productsLoading ? (
                            <p className="p-4 text-sm font-bold text-gray-500">Loading products...</p>
                          ) : productRows.length ? (
                            productRows.map((product) => (
                              <label className="flex cursor-pointer items-center gap-3 p-3 hover:bg-blue-50/40" key={product.id}>
                                <input
                                  checked={selectedProductIds.has(product.id)}
                                  disabled={!product.sku}
                                  onChange={() => toggleProduct(product.id)}
                                  type="checkbox"
                                />
                                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-gray-100 bg-gray-50">
                                  <img alt={product.name} className="max-h-10 max-w-10 object-contain" src={resolveProductImage(product.image)} />
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-sm font-black text-gray-900">{product.name}</span>
                                  <span className="block truncate text-xs font-bold text-gray-500">{product.sku || "No SKU"} - {product.category}</span>
                                </span>
                                <span className="rounded-lg bg-gray-100 px-2 py-1 text-xs font-black text-gray-600">{product.stock}</span>
                              </label>
                            ))
                          ) : (
                            <p className="p-4 text-sm font-bold text-gray-500">No products match this search.</p>
                          )}
                        </div>
                      </div>

                      <button
                        className="rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600 disabled:bg-gray-300"
                        disabled={!selectedWarehouse || !bulkBin || !selectedProducts.length}
                        type="submit"
                      >
                        Assign {selectedProducts.length || ""} Selected Product{selectedProducts.length === 1 ? "" : "s"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "transfers" && (
        <div className="grid gap-6 xl:grid-cols-[0.7fr_1.3fr]">
          <form className="rounded-2xl bg-white p-6 shadow-sm" onSubmit={transferStock}>
            <h2 className="text-xl font-black text-gray-900">Transfer Stock</h2>
            <div className="mt-5 grid gap-3">
              <select className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold" name="fromWarehouseId" onChange={updateTransferForm} value={transferForm.fromWarehouseId}>
                {warehouses.map((warehouse) => <option key={warehouse._id} value={warehouse._id}>{warehouse.name}</option>)}
              </select>
              <select className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold" name="toWarehouseId" onChange={updateTransferForm} value={transferForm.toWarehouseId}>
                {warehouses.map((warehouse) => <option key={warehouse._id} value={warehouse._id}>{warehouse.name}</option>)}
              </select>
              <input className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold" name="sku" onChange={updateTransferForm} placeholder="SKU" required value={transferForm.sku} />
              <div className="grid grid-cols-2 gap-3">
                <input className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold" min="1" name="quantity" onChange={updateTransferForm} placeholder="Quantity" required type="number" value={transferForm.quantity} />
                <input className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold" name="toBin" onChange={updateTransferForm} placeholder="To bin" value={transferForm.toBin} />
              </div>
              <button className="rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600" type="submit">
                Complete Transfer
              </button>
            </div>
          </form>

          <section className="rounded-2xl bg-white p-6 shadow-sm">
            <h2 className="text-xl font-black text-gray-900">Recent Transfers</h2>
            <div className="mt-5 grid gap-3">
              {transfers.length ? transfers.slice(0, 10).map((transfer, index) => (
                <div className="rounded-xl border border-gray-100 p-4" key={`${transfer._id || index}-${transfer.warehouseName}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-black text-gray-900">{transfer.sku}</p>
                      <p className="text-sm font-semibold text-gray-500">
                        {transfer.fromWarehouse} to {transfer.toWarehouse} - {transfer.quantity} units
                      </p>
                    </div>
                    <StatusPill tone="good">{transfer.status || "received"}</StatusPill>
                  </div>
                </div>
              )) : (
                <p className="rounded-xl bg-gray-50 p-4 text-sm font-bold text-gray-500">No warehouse transfers recorded yet.</p>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
